import { useCallback, useLayoutEffect, useRef, useState } from 'react';

import type { ExtractInjectionAPI } from '@atlaskit/editor-common/types';
import type { EditorView } from '@atlaskit/editor-prosemirror/view';
import { useSmartLinkContext } from '@atlaskit/link-provider/use-smart-link-context';
import type { SmartLinkResponse } from '@atlaskit/linking-types/smart-link';

import type { AIPlugin } from '../../../aiPluginType';
import { resolveSmartLinkForAI } from '../../smartlink-rovo/resolve-smartlink';
import { getIsAiEntryPointDisabled } from './use-is-ai-entry-point-disabled';

export type SmartLinkAIAction = 'rovo' | 'summarize' | 'dropdown';

export type SmartLinkAIActions = {
	details?: SmartLinkResponse;
	dispatchAction: (action: SmartLinkAIAction, itemTestId?: string) => Promise<void>;
	failed: boolean;
	isInaccessible: boolean;
	pendingAction?: SmartLinkAIAction;
	retryResolution: () => void;
};

type SmartLinkAIActionHandler = (
	action: SmartLinkAIAction,
	details: SmartLinkResponse,
	itemTestId?: string,
) => (() => void) | undefined;

export const useSmartLinkAIActions = ({
	api,
	disabled,
	getActionHandler,
	smartlinkUrl,
	view,
}: {
	api?: ExtractInjectionAPI<AIPlugin>;
	disabled: boolean;
	getActionHandler: SmartLinkAIActionHandler;
	smartlinkUrl: string;
	view: EditorView;
}): SmartLinkAIActions => {
	const context = useSmartLinkContext();
	const [resolvedDetails, setResolvedDetails] = useState<SmartLinkResponse>();
	const [pendingAction, setPendingAction] = useState<SmartLinkAIAction>();
	const [failed, setFailed] = useState(false);
	const resolutionScope = useRef<{ active: boolean } | undefined>(undefined);
	const pending = useRef(false);
	const latestProps = useRef({ api, disabled, getActionHandler, view });
	latestProps.current = { api, disabled, getActionHandler, view };

	const isCurrentScope = useCallback(
		(scope: { active: boolean } | undefined): scope is { active: boolean } =>
			scope?.active === true && resolutionScope.current === scope,
		[],
	);
	const loadResolution = useCallback(
		async (scope: { active: boolean }) => {
			try {
				const response = await resolveSmartLinkForAI(context, smartlinkUrl);
				if (!isCurrentScope(scope)) {
					return undefined;
				}
				setResolvedDetails(response);
				setFailed(false);
				return response;
			} catch {
				if (isCurrentScope(scope)) {
					setFailed(true);
				}
				return undefined;
			}
		},
		[context, isCurrentScope, smartlinkUrl],
	);

	useLayoutEffect(() => {
		const scope = { active: true };
		resolutionScope.current = scope;
		pending.current = false;
		setResolvedDetails(undefined);
		setPendingAction(undefined);
		setFailed(false);
		void loadResolution(scope);
		return () => {
			scope.active = false;
			if (resolutionScope.current === scope) {
				resolutionScope.current = undefined;
			}
		};
	}, [loadResolution]);

	const details = resolvedDetails ?? context.store?.getState()[smartlinkUrl]?.details;
	const isInaccessible = Boolean(details && details.meta?.access !== 'granted');

	const dispatchAction = useCallback(
		async (action: SmartLinkAIAction, itemTestId?: string) => {
			const scope = resolutionScope.current;
			const current = latestProps.current;
			const cachedDetails = context.store?.getState()[smartlinkUrl]?.details;
			if (
				!isCurrentScope(scope) ||
				pending.current ||
				current.disabled ||
				getIsAiEntryPointDisabled(current.api) ||
				Boolean(cachedDetails && cachedDetails.meta?.access !== 'granted')
			) {
				return;
			}
			const { doc, selection } = current.view.state;
			pending.current = true;
			setPendingAction(action);
			setFailed(false);
			try {
				const readyDetails = await loadResolution(scope);
				if (!readyDetails || !isCurrentScope(scope) || current.view.isDestroyed) {
					return;
				}
				const currentDetails = context.store?.getState()[smartlinkUrl]?.details ?? readyDetails;
				if (currentDetails.meta?.access !== 'granted') {
					return;
				}
				const latest = latestProps.current;
				if (
					latest.view !== current.view ||
					latest.disabled ||
					getIsAiEntryPointDisabled(latest.api) ||
					current.view.state.doc !== doc ||
					!current.view.state.selection.eq(selection)
				) {
					return;
				}
				latest.getActionHandler(action, currentDetails, itemTestId)?.();
			} catch {
				if (isCurrentScope(scope)) {
					setFailed(true);
				}
			} finally {
				if (isCurrentScope(scope)) {
					pending.current = false;
					setPendingAction(undefined);
				}
			}
		},
		[context, isCurrentScope, loadResolution, smartlinkUrl],
	);

	const retryResolution = useCallback(() => {
		const scope = resolutionScope.current;
		if (isCurrentScope(scope)) {
			setFailed(false);
			void loadResolution(scope);
		}
	}, [isCurrentScope, loadResolution]);

	return { details, dispatchAction, failed, isInaccessible, pendingAction, retryResolution };
};
